// ─── clientesSilencieuses.js ─────────────────────────────────────────
// Quelles clientes n'ont pas ouvert leur espace depuis un moment.
//
// L'INTENTION, ET CE QU'ELLE N'EST PAS
//
// Ce n'est pas de la surveillance. Anissa ne voit pas ce que fait une
// cliente dans l'application, et ce fichier ne calcule rien de tel. Il
// répond à une seule question : « qui risque de décrocher, et que je
// pourrais rappeler ? »
//
// D'où deux partis pris.
//
// On ne compte QUE les clientes dont le programme est publié. Une cliente
// qui attend encore son programme n'est pas silencieuse, elle attend. La
// signaler serait un reproche adressé à Anissa déguisé en alerte.
//
// On distingue « jamais connectée » de « ne revient plus ». Ce ne sont pas
// les mêmes situations : la première est souvent un accès qui n'est pas
// arrivé — le cas Rebecca du 21.08 —, la seconde un désengagement.

/** Au-delà, on considère qu'une cliente a décroché. */
export const SEUIL_SILENCE_JOURS = 14;

/** Délai de grâce après publication avant de s'inquiéter d'une non-connexion. */
export const DELAI_PREMIER_ACCES_JOURS = 3;

function joursDepuis(valeur, maintenant) {
  const t = Date.parse(valeur || '');
  if (!Number.isFinite(t)) return null;
  return Math.floor((maintenant.getTime() - t) / 86400000);
}

/**
 * @typedef {Object} ClienteObservee
 * @property {string} id
 * @property {string} prenom
 * @property {boolean} programmePublie
 * @property {string|null} publieLe
 * @property {string|null} derniereConnexion
 */

/**
 * Trie les clientes entre celles qui n'ont jamais ouvert leur espace et
 * celles qui n'y reviennent plus.
 *
 * @param {ClienteObservee[]} clientes
 * @param {Date} [maintenant]
 * @returns {{
 *   jamaisVenues: Array<object>,
 *   decrochees: Array<object>,
 *   total: number,
 * }}
 */
export function clientesSilencieuses(clientes = [], maintenant = new Date()) {
  const jamaisVenues = [];
  const decrochees = [];

  for (const c of Array.isArray(clientes) ? clientes : []) {
    // Sans programme publié, il n'y a rien à ouvrir : ce n'est pas du
    // silence, c'est une attente légitime.
    if (!c?.programmePublie) continue;

    const depuisConnexion = joursDepuis(c.derniereConnexion, maintenant);

    if (depuisConnexion === null) {
      // Jamais connectée. On laisse quelques jours après la publication
      // avant de s'en inquiéter : personne n'ouvre son espace dans l'heure.
      const depuisPublication = joursDepuis(c.publieLe, maintenant);
      if (depuisPublication !== null && depuisPublication >= DELAI_PREMIER_ACCES_JOURS) {
        jamaisVenues.push({ ...c, jours: depuisPublication });
      }
      continue;
    }

    if (depuisConnexion >= SEUIL_SILENCE_JOURS) {
      decrochees.push({ ...c, jours: depuisConnexion });
    }
  }

  // La plus silencieuse d'abord : c'est celle qu'il faut rappeler en premier.
  const parAnciennete = (a, b) => b.jours - a.jours;
  jamaisVenues.sort(parAnciennete);
  decrochees.sort(parAnciennete);

  return Object.freeze({
    jamaisVenues: Object.freeze(jamaisVenues),
    decrochees: Object.freeze(decrochees),
    total: jamaisVenues.length + decrochees.length,
  });
}

/** Mise en mots, au singulier comme au pluriel. */
export function formulerSilence(jours) {
  if (jours < 21) return `${jours} jours`;
  const semaines = Math.floor(jours / 7);
  if (semaines < 9) return `${semaines} semaines`;
  const mois = Math.floor(jours / 30);
  return mois <= 1 ? 'plus d’un mois' : `${mois} mois`;
}
